import { Component, EventEmitter, Input, Output } from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
	FinancialInstitutionLender,
	MortgageeType,
	PopularCSLenders,
	PopularLenders,
} from '@models/lender';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import {
	AlgoliaFinancialInstitution,
	AlgoliaFinancialInstitutionBranch,
} from '@fnModels/algolia/financial_institutions';
import { take } from 'rxjs/operators';
import { Action } from '@state/utils/models';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import {
	FinancialInstitutionSearchDispatch,
	FinancialInstitutionSearchState,
} from '@state/algolia-search/financial-institution-search';
import { FinancialInstitutionBranchSearchState } from '@state/algolia-search/financial-institution-branch-search';
import { MatterFileType } from '@models/matter';
import { CreateOrUpdateInstitutionModel } from 'src/app/private/sale/encumbrances/encumbrance/components/institution/create-branch/create-branch.component';
import { ICON } from '@core/util/icon';
import { MatDialog } from '@angular/material/dialog';
import { InstitutionRepo } from '@repos/institution/institution.service';
import { InstitutionBranchRepo } from '@repos/institutionBranch/institution-branch.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import LogRocket from 'logrocket';
import {
	CreateInstitutionComponent,
	CreateInstitutionData,
} from 'src/app/private/sale/encumbrances/encumbrance/components/institution/create-institution/create-institution.component';
import { Branch } from '@models/lender';
import { OnSelectBranch } from '../branch-search/branch-search.component';
import { computerShareInstitutionId } from '../../lender-selector/lender-selector.types';
import {
	DropdownSelect,
	onSelectInstitution,
} from './lender-search-input.types';

@UntilDestroy()
@Component({
	selector: 'app-lender-search-input',
	templateUrl: './lender-search-input.component.html',
	animations: [openClose(), fade()],

	standalone: false,
})
export class LenderSearchInputComponent {
	public loading = false;
	public ICON = ICON;
	public financialInstitutionName = '';
	public institutionModel: CreateOrUpdateInstitutionModel;
	public dropdownSelect = DropdownSelect;
	public lenders = PopularLenders;
	public popularCSLenders = PopularCSLenders;
	public mortgageeTypes = MortgageeType;

	@Output() onSelected = new EventEmitter<onSelectInstitution>();

	@Output() onCreate = new EventEmitter<string>();

	@Input() mortgageeType: MortgageeType = MortgageeType.INSTITUTION;
	@Input() includeFinancialInstitutions: boolean;
	@Input() includeTrustCompanies: boolean;
	@Input() fileType: MatterFileType;
	@Input() showDischargeFields: boolean;

	@ViewSelectSnapshot(FinancialInstitutionSearchState.getList)
	financialInstitutions;
	@ViewSelectSnapshot(FinancialInstitutionBranchSearchState.getList)
	financialInstitutionsBranches;

	constructor(
		private institutionDispatch: FinancialInstitutionSearchDispatch,
		private matDialog: MatDialog,
		private institutionRepo: InstitutionRepo,
		private branchRepo: InstitutionBranchRepo,
		private snackbar: SnackbarService,
	) {}

	search = (ev) => {
		this.institutionDispatch.Search(ev, {
			creditUnion: true,
			generalFinancialInstitution: true,
			trustCompany: true,
		});
	};

	onSelect = async (
		ev: DropdownResult<
			Partial<AlgoliaFinancialInstitution | AlgoliaFinancialInstitutionBranch>
		>,
	) => {
		this.loading = true;
		if (ev.groupType === DropdownSelect.SELECT_INSTITUTION) {
			try {
				// fetch institution
				const _doc = await this.institutionRepo
					.Get((ev.result as AlgoliaFinancialInstitution)?.id)
					.pipe(take(1))
					.toPromise();

				this.onSelected.emit({
					financialInstitution: {
						...(_doc.data() as FinancialInstitutionLender),
						id: _doc.id,
					},
					action: Action.SET,
				});
			} catch (err) {
				LogRocket.error('Unable to select financial institution', err);
				console.log('Unable to select financial institution', err);
			} finally {
				this.loading = false;
			}
		} else if (ev.groupType === DropdownSelect.SELECT_BRANCH) {
			try {
				const _doc = await this.institutionRepo
					.Get((ev.result as AlgoliaFinancialInstitutionBranch)?.institutionId)
					.pipe(take(1), untilDestroyed(this))
					.toPromise();
				const _branchDoc = await this.branchRepo
					.Get((ev.result as AlgoliaFinancialInstitutionBranch)?.id, {
						financialInstitutionId: (
							ev.result as AlgoliaFinancialInstitutionBranch
						)?.institutionId,
					})
					.pipe(take(1), untilDestroyed(this))
					.toPromise();

				const institution = {
					..._doc.data(),
					id: _doc.id,
					branch: { ..._branchDoc.data(), id: _branchDoc.id },
				};

				this.onSelected.emit({
					financialInstitution: institution as FinancialInstitutionLender,
					action: Action.SET,
				});
			} catch (err) {
				LogRocket.error('Unable to select financial institution branch', err);
				console.log('Unable to select financial institution branch', err);
			} finally {
				this.loading = false;
			}
		} else if (ev.groupType === DropdownSelect.CREATE) {
			// load create component
			const data: CreateInstitutionData = {
				showDischargeFields: this.showDischargeFields,
				fileType: this.fileType,
				create: true,
				institution: {
					name: this.financialInstitutionName,
				},
				type: this.mortgageeType,
			};
			this.matDialog
				.open(CreateInstitutionComponent, {
					data,
					width: '80vw',
					maxWidth: '800px',
				})
				.afterClosed()
				.pipe(take(1), untilDestroyed(this))
				.subscribe((result) => {
					if (result) {
						this.onSelected.emit({
							financialInstitution: result as FinancialInstitutionLender,
							action: Action.CREATE,
						});
						this.snackbar.openSnackBar({
							text: 'Lender details submitted for review.',
							type: 'success',
						});
					}
					this.loading = false;
				});
		}
	};

	setLeadingBank(institutionId: string) {
		this.onSelect({
			result: { id: institutionId },
			groupType: DropdownSelect.SELECT_INSTITUTION,
			group: 0,
		});
	}

	onSelectComputerShareBranch(selectedBranch: OnSelectBranch) {
		this.onSelected.emit({
			action: selectedBranch.action,
			financialInstitution: {
				branch: selectedBranch.branch,
			} as FinancialInstitutionLender,
		});
	}

	async setLeadingCSLender(branchId: string) {
		try {
			const branchResponse = await this.branchRepo
				.Get(branchId, { financialInstitutionId: computerShareInstitutionId })
				.pipe(take(1))
				.toPromise();
			const branch = {
				...branchResponse.data(),
				id: branchResponse.id,
				logoUrl: branchResponse.data()?.logoUrl ?? null,
			} as Branch;

			this.onSelected.emit({
				action: Action.SET,
				financialInstitution: {
					branch: branch,
				} as FinancialInstitutionLender,
			});
		} catch (err) {
			console.log(err);
			LogRocket.error('Error fetching institution with id', branchId, err);
		}
	}
}
